import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Avatar,
  CircularProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  CalendarToday,
  FilterList,
  Download,
  Refresh,
  AttachMoney,
  ShoppingCart,
  TrendingUp,
  People,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  ShowChart,
  TableChart,
  ErrorOutline
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format, subDays, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import reportService from '../services/reportService';

const SalesReport = () => {
  const { t } = useTranslation();
  const MotionDiv = motion.div;
  
  // State for date range
  const [startDate, setStartDate] = useState(new Date(2025, 4, 1)); // May 1, 2025
  const [endDate, setEndDate] = useState(new Date());
  
  // State for filter by
  const [filterType, setFilterType] = useState('monthly');
  
  // State for sales metrics
  const [salesMetrics, setSalesMetrics] = useState({
    total_revenue: 0,
    total_orders: 0,
    avg_order_value: 0,
    avg_items_per_order: 0
  });
  
  // State for chart data
  const [chartData, setChartData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [topSellingItems, setTopSellingItems] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for chart type
  const [chartType, setChartType] = useState('line');
  
  // State for view type
  const [viewType, setViewType] = useState('chart');
  
  // Define colors for pie charts
  const COLORS = ['#667eea', '#764ba2', '#63b3ed', '#4c51bf', '#48bb78', '#38b2ac', '#ed8936', '#ed64a6'];

  useEffect(() => {
    const fetchReportData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Test API connection
        try {
          await reportService.testConnection();
          console.log("API connection test successful");
        } catch (connErr) {
          console.error("API connection test failed:", connErr);
          setError(t('reports.connectionError'));
          setIsLoading(false);
          return;
        }
  
        // Format dates for API requests
        const formattedStartDate = format(startDate, 'yyyy-MM-dd');
        const formattedEndDate = format(endDate, 'yyyy-MM-dd');
        
        console.log("Fetching data with date range:", formattedStartDate, "to", formattedEndDate);
        
        // Fetch sales graph data
        const salesGraphData = await reportService.getSalesGraphByDates(
          formattedStartDate, 
          formattedEndDate,
          filterType
        );
        
        console.log("Sales graph data:", salesGraphData);
        setChartData(salesGraphData || []);
        
        // Fetch order summary data
        const orderSummaryData = await reportService.getOrderSummaryByDates(
          formattedStartDate, 
          formattedEndDate
        );
        
        console.log("Order summary data:", orderSummaryData);
        if (orderSummaryData && orderSummaryData.length > 0) {
          setSalesMetrics(orderSummaryData[0]);
        }
        
        // Fetch and process category data
        let categoryData = await reportService.getSalesByCategory(
          formattedStartDate, 
          formattedEndDate
        );
        
        // Process categoryData to ensure correct format for pie chart
        if (categoryData && categoryData.length > 0) {
          // Create new objects with only name and value (as number)
          categoryData = categoryData.map(item => ({
            name: item.name || t('reports.unknown'),
            value: Number(item.value || item.amount || item.total || 0)
          }));
          console.log("Category data processed:", categoryData);
        } else {
          console.log("No category data available");
          categoryData = [];
        }
        setCategoryData(categoryData);
        
        // Fetch and process payment method data
        let paymentMethodData = await reportService.getSalesByPaymentMethod(
          formattedStartDate, 
          formattedEndDate
        );
        
        // Process paymentMethodData to ensure correct format for pie chart
        if (paymentMethodData && paymentMethodData.length > 0) {
          // Create new objects with only name and value (as number)
          paymentMethodData = paymentMethodData.map(item => ({
            name: item.name || t('reports.unknown'),
            value: Number(item.value || item.amount || item.total || 0)
          }));
          console.log("Payment method data processed:", paymentMethodData);
        } else {
          console.log("No payment method data available");
          paymentMethodData = [];
        }
        setPaymentMethodData(paymentMethodData);
        
        // Fetch top selling items
        const topItemsData = await reportService.getTopMenuItemByDates(
          formattedStartDate, 
          formattedEndDate
        );
        
        console.log("Top items data:", topItemsData);
        setTopSellingItems(topItemsData || []);
        
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError(t('reports.loadError', { error: err.message || t('reports.unknownError') }));
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchReportData();
  }, [startDate, endDate, filterType, t]);
  
  // Handle date range change
  const handleDateRangeChange = (range) => {
    const today = new Date();
    
    switch (range) {
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case 'yesterday': {
        const yesterday = subDays(today, 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
        break;
      }
      case 'last7days':
        setStartDate(subDays(today, 6));
        setEndDate(today);
        break;
      case 'last30days':
        setStartDate(subDays(today, 29));
        setEndDate(today);
        break;
      case 'last3months':
        setStartDate(subMonths(today, 3));
        setEndDate(today);
        break;
      case 'thisWeek':
        setStartDate(startOfWeek(today, { weekStartsOn: 1 }));
        setEndDate(endOfWeek(today, { weekStartsOn: 1 }));
        break;
      case 'thisMonth':
        setStartDate(startOfMonth(today));
        setEndDate(endOfMonth(today));
        break;
      case 'thisYear':
        setStartDate(startOfYear(today));
        setEndDate(endOfYear(today));
        break;
      default:
        // Custom range - don't change dates
        break;
    }
  };
  
  // Handle filter type change
  const handleFilterTypeChange = (event) => {
    setFilterType(event.target.value);
  };
  
  // Handle chart type change
  const handleChartTypeChange = (event, newValue) => {
    setChartType(newValue);
  };
  
  // Handle view type change
  const handleViewTypeChange = (event, newValue) => {
    setViewType(newValue);
  };
  
  // Format date for display
  const formatDate = (date) => {
    return format(date, 'MMM dd, yyyy');
  };
  
  // Generate PDF report
  const handleGeneratePDF = () => {
    console.log('Generate PDF report');
    alert(t('reports.pdfFeatureMessage'));
  };
  
  // Export as CSV
  const handleExportCSV = () => {
    console.log('Export as CSV');
    alert(t('reports.csvFeatureMessage'));
  };
  
  // Refresh data
  const handleRefresh = () => {
    // Simply trigger a re-fetch by using same effect logic
    setIsLoading(true);
    // Re-fetch will be handled by the useEffect
  };
  
  // Render chart based on chart type
  const renderChart = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: 400 
        }}>
          <ErrorOutline sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {error}
          </Typography>
        </Box>
      );
    }
    
    if (chartData.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: 400 
        }}>
          <ErrorOutline sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('reports.noDataAvailable')}
          </Typography>
        </Box>
      );
    }
    
    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="time_group" />
              <YAxis 
                domain={[0, 'auto']} 
                allowDataOverflow={true}
              />
              <Tooltip 
                formatter={(value) => [`$${value}`, t('reports.sales')]} 
                contentStyle={{ 
                  borderRadius: 8, 
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                }}
              />
              <Legend />
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#667eea" />
                  <stop offset="100%" stopColor="#764ba2" />
                </linearGradient>
              </defs>
              <Line 
                type="monotone" 
                dataKey="total_sales" 
                name={t('reports.sales')}
                stroke="url(#colorGradient)" 
                strokeWidth={3} 
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="time_group" />
              <YAxis domain={[0, 'auto']} />
              <Tooltip 
                formatter={(value) => [`$${value}`, t('reports.sales')]} 
                contentStyle={{ 
                  borderRadius: 8, 
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                }}
              />
              <Legend />
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#764ba2" />
                  <stop offset="100%" stopColor="#667eea" />
                </linearGradient>
              </defs>
              <Bar 
                dataKey="total_sales" 
                name={t('reports.sales')}
                fill="url(#barGradient)" 
                radius={[4, 4, 0, 0]} 
              />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'pie':
        console.log('Category data before render:', JSON.stringify(categoryData));
        console.log('Payment data before render:', JSON.stringify(paymentMethodData));
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('reports.salesByCategory')}
                </Typography>
                {categoryData.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 300 
                  }}>
                    <Typography color="text.secondary">{t('reports.noCategoryData')}</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-category-${index}-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`$${value.toFixed(2)}`, t('reports.sales')]} 
                        contentStyle={{ 
                          borderRadius: 8, 
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('reports.salesByPaymentMethod')}
                </Typography>
                {paymentMethodData.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: 300 
                  }}>
                    <Typography color="text.secondary">{t('reports.noPaymentData')}</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={paymentMethodData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {paymentMethodData.map((entry, index) => (
                          <Cell key={`cell-payment-${index}-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`$${value.toFixed(2)}`, t('reports.sales')]} 
                        contentStyle={{ 
                          borderRadius: 8, 
                          border: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };
  
  // Render data table
  const renderTable = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error || chartData.length === 0) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          py: 4 
        }}>
          <Typography color="text.secondary">
            {error || t('reports.noDataAvailable')}
          </Typography>
        </Box>
      );
    }
    
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('reports.table.period')}</TableCell>
              <TableCell align="right">{t('reports.table.orders')}</TableCell>
              <TableCell align="right">{t('reports.table.salesAmount')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chartData.map((row, index) => (
              <TableRow key={`row-${index}-${row.time_group}`} hover>
                <TableCell component="th" scope="row">
                  {row.time_group}
                </TableCell>
                <TableCell align="right">{row.total_orders}</TableCell>
                <TableCell align="right">${parseFloat(row.total_sales).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {t('reports.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {`${formatDate(startDate)} - ${formatDate(endDate)}`}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              {t('reports.refresh')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportCSV}
            >
              {t('reports.exportCSV')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleGeneratePDF}
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                }
              }}
            >
              {t('reports.generatePDF')}
            </Button>
          </Box>
        </Box>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('reports.dateRange')}</InputLabel>
                  <Select
                    label={t('reports.dateRange')}
                    defaultValue="last3months"
                    onChange={(e) => handleDateRangeChange(e.target.value)}
                  >
                    <MenuItem value="today">{t('reports.today')}</MenuItem>
                    <MenuItem value="yesterday">{t('reports.yesterday')}</MenuItem>
                    <MenuItem value="last7days">{t('reports.last7Days')}</MenuItem>
                    <MenuItem value="last30days">{t('reports.last30Days')}</MenuItem>
                    <MenuItem value="last3months">{t('reports.last3Months')}</MenuItem>
                    <MenuItem value="thisWeek">{t('reports.thisWeek')}</MenuItem>
                    <MenuItem value="thisMonth">{t('reports.thisMonth')}</MenuItem>
                    <MenuItem value="thisYear">{t('reports.thisYear')}</MenuItem>
                    <MenuItem value="custom">{t('reports.customRange')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label={t('reports.fromDate')}
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setStartDate(new Date(e.target.value));
                    } else {
                      setStartDate(null);
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label={t('reports.toDate')}
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      setEndDate(new Date(e.target.value));
                    } else {
                      setEndDate(null);
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('reports.groupBy')}</InputLabel>
                  <Select
                    value={filterType}
                    onChange={handleFilterTypeChange}
                    label={t('reports.groupBy')}
                  >
                    <MenuItem value="hourly">{t('reports.hourly')}</MenuItem>
                    <MenuItem value="daily">{t('reports.daily')}</MenuItem>
                    <MenuItem value="weekly">{t('reports.weekly')}</MenuItem>
                    <MenuItem value="monthly">{t('reports.monthly')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                    <AttachMoney />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('reports.totalSales')}
                  </Typography>
                </Box>
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    ${parseFloat(salesMetrics.total_revenue || 0).toFixed(2)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.light', mr: 2 }}>
                    <ShoppingCart />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('reports.totalOrders')}
                  </Typography>
                </Box>
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {salesMetrics.total_orders || 0}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.light', mr: 2 }}>
                    <TrendingUp />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('reports.avgOrderValue')}
                  </Typography>
                </Box>
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    ${parseFloat(salesMetrics.avg_order_value || 0).toFixed(2)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Tabs value={chartType} onChange={handleChartTypeChange}>
                <Tab value="line" icon={<ShowChart />} label={t('reports.line')} />
                <Tab value="bar" icon={<BarChartIcon />} label={t('reports.bar')} />
                <Tab value="pie" icon={<PieChartIcon />} label={t('reports.pie')} />
              </Tabs>
              
              <Tabs value={viewType} onChange={handleViewTypeChange}>
                <Tab value="chart" icon={<ShowChart />} label={t('reports.chart')} />
                <Tab value="table" icon={<TableChart />} label={t('reports.table')} />
              </Tabs>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              {viewType === 'chart' ? renderChart() : renderTable()}
            </Box>
          </CardContent>
        </Card>
      </MotionDiv>
      
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('reports.topSellingItems')}
            </Typography>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : topSellingItems.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">{t('reports.noSalesData')}</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('reports.topItems.itemName')}</TableCell>
                      <TableCell align="right">{t('reports.topItems.quantitySold')}</TableCell>
                      <TableCell align="right">{t('reports.topItems.totalSales')}</TableCell>
                      <TableCell align="center">{t('reports.topItems.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topSellingItems.map((item, index) => (
                      <TableRow key={`item-${item.name}-${index}`} hover>
                        <TableCell component="th" scope="row">
                          {item.name}
                        </TableCell>
                        <TableCell align="right">{item.total_qty}</TableCell>
                        <TableCell align="right">${parseFloat(item.total_revenue).toFixed(2)}</TableCell>
                        <TableCell align="center">
                          {item.available ? (
                            <Typography 
                              component="span" 
                              sx={{ 
                                color: 'success.main', 
                                bgcolor: 'success.light', 
                                px: 1, 
                                py: 0.5, 
                                borderRadius: 1,
                                fontSize: '0.75rem'
                              }}
                            >
                              {t('status.available')}
                            </Typography>
                          ) : (
                            <Typography 
                              component="span" 
                              sx={{ 
                                color: 'error.main', 
                                bgcolor: 'error.light', 
                                px: 1, 
                                py: 0.5, 
                                borderRadius: 1,
                                fontSize: '0.75rem'
                              }}
                            >
                              {t('status.unavailable')}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </MotionDiv>
    </Box>
  );
};

export default SalesReport;